// Progress: a bar in a recessed track that springs to its new length, with an optional figure
// beside it ("n of m" or "n%") that ticks. Three words can show beside the bar, one at a time:
// `over` past the total, `warn` at a threshold (both the warning tone), and `success` at full
// (the done green), so colour never carries a state alone. `glow` lights the one bar that is
// the thing (the limiting line, the live upload). `celebrate` turning true plays one light
// sweep across the bar, for the moment a run of work completes.
// docs/operations/design-standard.md, "Motion" and "Charm".

import React, { useEffect, useRef, useState } from 'react';
import { useTicker } from './motion';

export type ProgressSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ProgressProps {
  value: number;
  max: number;
  /** What is progressing, for the progressbar's accessible name. */
  label: string;
  /** The figure beside the bar: `count` is "n of m", `percent` is "n%". No figure without it. */
  display?: 'count' | 'percent';
  /** A word after the figure, held in its slot: "cleared" reads "12 of 40 cleared". */
  unit?: string;
  /**
   * At or past `at` (in the bar's own units) the fill takes the warning tone and `label` shows
   * beside the bar, so colour never carries the state alone. The word's slot is held below
   * the line too, so a column of bars stays one length.
   */
  warn?: { at: number; label: string };
  /** Past `max` (over-billed, over-delivered): the bar stays full in the warning tone and this word shows. */
  over?: string;
  /** At full the fill turns the done green. A string also shows as the word beside it ("Ready"). */
  success?: boolean | string;
  /** `neutral` for an amount that is not progress toward done, such as a workload. */
  tone?: 'primary' | 'neutral';
  /** A category's own colour (data, a hex), only where the row names the category beside it. */
  color?: string;
  /** Track height: `xs` 2px under a status line, `sm` 4px in dense cards, `md` 6px, `lg` 8px as a record's headline, `xl` 14px on a wall display. */
  size?: ProgressSize;
  /** The bar gives off accent light: the one that matters among its neighbours. */
  glow?: boolean;
  /** Read out in place of "n of m", for a bar whose max is not a total. */
  valueText?: string;
  /** Holds the fill's movement, so a column of bars can fill in turn. */
  delayMs?: number;
  /** Starts empty and fills to its value once it is on screen. */
  fillIn?: boolean;
  /** Plays the sweep each time this turns from false to true. */
  celebrate?: boolean;
  className?: string;
}

const FILL = { primary: 'bg-primary', neutral: 'bg-on-surface-variant' } as const;
const TRACK: Record<ProgressSize, string> = { xs: 'h-0.5', sm: 'h-1', md: 'h-1.5', lg: 'h-2', xl: 'h-3.5' };
// A value above zero is never drawn as nothing.
const SLIVER = 0.02;

/** Holds the width of `widest`, so the bar beside a ticking figure never changes length. */
const Slot: React.FC<{ widest: string; hidden?: boolean; className: string; children: React.ReactNode }> = ({ widest, hidden, className, children }) => (
  <span aria-hidden={hidden || undefined} className={`grid flex-none whitespace-nowrap tabular-nums ${className}`}>
    <span aria-hidden="true" className="invisible col-start-1 row-start-1">{widest}</span>
    <span className="col-start-1 row-start-1 text-right">{children}</span>
  </span>
);

export const Progress: React.FC<ProgressProps> = ({
  value, max, label, display, unit, warn, over, success, tone = 'primary', color, size = 'md', glow,
  valueText, delayMs, fillIn, celebrate, className = '',
}) => {
  const done = Math.max(0, Math.min(value, max));
  const isOver = max > 0 && value > max;
  // The real figure shows past max only when the caller named the overshoot; otherwise it reads as full.
  const shownValue = isOver && over ? value : done;
  const full = max > 0 && value >= max;
  const frac = max > 0 ? done / max : 0;
  // Floored below the top: 199 of 200 is 99%, never a 100% that is not done. Past the top it is the real figure.
  const pct = max <= 0 ? 0 : isOver && over ? Math.round((value / max) * 100) : done < max ? Math.min(99, Math.round(frac * 100)) : 100;
  const counted = useTicker(Math.max(0, shownValue));
  const pctShown = useTicker(pct);
  // The raw value, so a threshold past max can still fire; nothing warns on an empty bar.
  const warned = !!warn && value > 0 && value >= warn.at;
  const succeeded = !!success && full && !isOver;
  const state: { word: string; tone: string } | null =
    isOver && over ? { word: over, tone: 'text-orange-300' }
      : warned && warn ? { word: warn.label, tone: 'text-orange-300' }
        : succeeded && typeof success === 'string' ? { word: success, tone: 'text-emerald-300' }
          : null;
  // Kept after the state clears, so the word fades out instead of vanishing.
  const lastState = useRef(state);
  if (state) lastState.current = state;
  const shown = state ?? lastState.current;
  const words = [over, warn?.label, typeof success === 'string' ? success : undefined].filter((w): w is string => !!w);
  const widestWord = words.reduce((a, b) => (b.length > a.length ? b : a), '');

  const [primed, setPrimed] = useState(!fillIn);
  useEffect(() => {
    if (primed) return undefined;
    // Two frames: the empty bar has to paint once or nothing fills.
    if (typeof requestAnimationFrame !== 'function') { setPrimed(true); return undefined; }
    let second = 0;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setPrimed(true)); });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, [primed]);

  const [sweep, setSweep] = useState(0);
  const was = useRef(!!celebrate);

  useEffect(() => {
    const now = !!celebrate;
    if (now && !was.current) setSweep((n) => n + 1);
    was.current = now;
  }, [celebrate]);

  useEffect(() => {
    if (!sweep) return undefined;
    // Timed rather than on animationend, which never arrives when the animation does not run.
    const t = window.setTimeout(() => setSweep(0), 800);
    return () => window.clearTimeout(t);
  }, [sweep]);

  const said = valueText ?? (display === 'percent' ? `${pct}%` : `${Math.max(0, shownValue)} of ${max}`);
  const drawn = !primed ? 0 : isOver ? 1 : done > 0 ? Math.max(frac, SLIVER) : 0;
  const hue = isOver && over ? 'bg-orange-500' : warned ? 'bg-orange-500' : succeeded ? 'bg-emerald-400' : color ? '' : FILL[tone];
  const figure = size === 'xl' ? 'text-sm' : 'text-xs';
  const top = Math.max(max, shownValue);

  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <div className="relative min-w-0 flex-1">
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-inner shadow-glow transition-opacity duration-standard ease-emphasized ${glow ? 'opacity-100' : 'opacity-0'}`}
        />
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={done}
          aria-valuetext={state ? `${said}, ${state.word}` : said}
          className={`relative overflow-hidden rounded-inner bg-surface-container-highest shadow-recess ${TRACK[size]}`}
        >
          <div
            className={`absolute inset-0 origin-left rounded-inner transition-[transform,background-color] duration-spring ease-spring motion-reduce:transition-[background-color] ${hue}`}
            style={{
              transform: `scaleX(${drawn})`,
              transitionDelay: delayMs ? `${delayMs}ms` : undefined,
              backgroundColor: color && !hue ? color : undefined,
            }}
          />
          {sweep > 0 && (
            <div
              key={sweep}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.6),transparent)] motion-safe:animate-sheen motion-reduce:animate-flare"
            />
          )}
        </div>
      </div>
      {words.length > 0 && (
        <Slot widest={widestWord} hidden className={`${figure} ${shown?.tone ?? ''}`}>
          <span className={`transition-opacity duration-standard ease-emphasized ${state ? 'opacity-100' : 'opacity-0'}`}>{shown?.word ?? ''}</span>
        </Slot>
      )}
      {display === 'count' && (
        <Slot widest={`${top} of ${max}${unit ? ` ${unit}` : ''}`} className={`${figure} text-on-surface-variant`}>
          {counted} of {max}{unit ? ` ${unit}` : ''}
        </Slot>
      )}
      {display === 'percent' && (
        <Slot widest={`${Math.max(100, pct)}%${unit ? ` ${unit}` : ''}`} className={`${figure} text-on-surface-variant`}>
          {pctShown}%{unit ? ` ${unit}` : ''}
        </Slot>
      )}
    </div>
  );
};

export default Progress;
